import React from 'react'
import { Card } from 'react-bootstrap'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import IDonor from '@interfaces/IDonor'
import ICustomImportDonor from '@interfaces/ICustomImportDonor'
import DonorPrioritySection from './sections/DonorPrioritySection'
import DonorInfoSection from './sections/DonorInfoSection'
import DonorBankLogo from './sections/DonorBankLogo'
import DonorTypeSection from './sections/DonorTypeSection'
import DonorGeneticsSection from './sections/DonorGeneticsSection'
import DonorPhotomatchBadge from './sections/DonorPhotomatchBadge'
import DonorTreatments from './sections/DonorTreatments'
import DonorRemoveButton from './sections/DonorRemoveButton'
import './DonorCard.css'

export type DonorCardVariant = 'selection' | 'checkout' | 'reservation'
export type TreatmentDisplayMode = 'all' | 'selected-only' | 'reserved'

export interface DonorCardBaseProps {
  donor: IDonor | ICustomImportDonor
  position: number
  variant: DonorCardVariant
  treatmentDisplayMode: TreatmentDisplayMode
  selectedTreatment?: string
  isDraggable?: boolean
  isLast?: boolean
  showPhotoMatchColumn?: boolean
  isPhotoMatchEligible?: boolean
  disableLink?: boolean
  onRemove?: (id: string) => void
}

const DonorCardBase: React.FC<DonorCardBaseProps> = ({
  donor,
  position,
  variant,
  treatmentDisplayMode,
  selectedTreatment,
  isDraggable = false,
  isLast = false,
  showPhotoMatchColumn = false,
  isPhotoMatchEligible = false,
  disableLink = false,
  onRemove,
}) => {
  const availableTreatments = donor?.available_treatment_names ?? []
  const mappedSelectedTreatment = selectedTreatment === 'Inseminação' ? 'IIU' : selectedTreatment
  const isTreatmentEligible = !mappedSelectedTreatment || availableTreatments.includes(mappedSelectedTreatment)
  const isNational = donor?.sperm_bank?.name === 'Pro-Seed'

  const isReservedMode = treatmentDisplayMode === 'reserved'

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(donor.internal_id),
    disabled: !isDraggable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: isDragging ? 'relative' : 'static',
  } as React.CSSProperties

  const cardClassName = [
    'donor-card',
    'large-card',
    `donor-card--${variant}`,
    isLast ? 'last-donor' : '',
    isReservedMode || isTreatmentEligible ? 'border-none' : 'border-red',
    isDraggable ? 'is-draggable' : '',
  ].filter(Boolean).join(' ')

  return (
    <Card className={cardClassName} ref={setNodeRef} style={style}>
      <Card.Header className="card-header-donator" id="card-swiper" />

      <Card.Body className="card-body-donator">
        <div className="donor-card__wrapper">
          <div className="donor-card__header">
            <DonorPrioritySection
              position={position}
              isDraggable={isDraggable}
              sortableProps={isDraggable ? { attributes, listeners } : undefined}
            />

            <DonorInfoSection
              donor={donor}
              isNational={isNational}
              showPhotoMatch={showPhotoMatchColumn}
              isPhotoMatchEligible={isPhotoMatchEligible}
              disableLink={disableLink}
            />

            {onRemove && (
              <DonorRemoveButton onRemove={() => onRemove(String(donor.internal_id))} />
            )}
          </div>

          <div className="donor-card__content">
            <DonorBankLogo bankName={donor?.sperm_bank?.name} />

            <DonorTypeSection
              donorType={donor?.donor_type ?? undefined}
              donorId={donor.internal_id}
            />

            <DonorGeneticsSection
              genetics={(donor as IDonor).genetics}
              hasTecle={Boolean((donor as IDonor).has_tecle) && position === 1}
              donorId={donor.internal_id}
            />

            {showPhotoMatchColumn && (
              <DonorPhotomatchBadge isEligible={isPhotoMatchEligible} />
            )}
          </div>

          <div className="donor-card__treatments">
            <DonorTreatments
              donor={donor}
              availableTreatments={availableTreatments}
              selectedTreatment={selectedTreatment}
              treatmentDisplayMode={treatmentDisplayMode}
              isTreatmentEligible={isTreatmentEligible}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export default DonorCardBase
